import { useState, useRef } from "react";
import { useMountEffect, useResizeObserver } from "@react-hookz/web";
import { useAnimationFrame, useTime } from "framer-motion";

export function useAnimation(countdownSeconds: number) {
  const circle = useRef<SVGCircleElement>(null);
  const [pathLength, setPathLength] = useState(0);
  const [countdown, setCountdown] = useState(countdownSeconds);
  const time = useTime();
  const strokeDashOffset = (percentange: number) =>
    `${(pathLength / 100) * (100 - percentange * 100)}px`;

  /**
   * Monitor Path Length
   */
  useMountEffect(() => {
    if (!circle.current) {
      return;
    }

    const length = circle.current.getTotalLength();

    setPathLength(length);
  });

  useResizeObserver(circle, () => {
    if (!circle.current) {
      return;
    }

    const length = circle.current.getTotalLength();

    setPathLength(length);
  });

  /**
   * Keep track of the countdown
   */
  useAnimationFrame(() => {
    const nextCountDown = convertTimeToCountdownNumber(
      time.get(),
      countdownSeconds
    );

    if (nextCountDown !== countdown) {
      setCountdown(nextCountDown);
    }

    // manually animate the stroke-dashoffset because framer won't for some reason and their path helpers aren't working either
    if (nextCountDown <= 0) {
      circle.current?.removeAttribute("stroke-dashoffset");

      return;
    }

    const percentageOfSecond = (time.get() / 1000) % 1;

    circle.current?.setAttribute(
      "stroke-dashoffset",
      strokeDashOffset(percentageOfSecond)
    );
  });

  return {
    ref: circle,
    countdown,
    pathLength,
  };
}

// converts the growing time number into a countdown number to display with a min of 0
function convertTimeToCountdownNumber(time: number, countdownSeconds: number) {
  if (time > countdownSeconds * 1000) {
    return 0;
  }

  const countdownValue = Math.floor(time / 1000) % (countdownSeconds + 1);
  const outputValue = countdownSeconds - countdownValue;

  if (outputValue < 0) {
    return 0;
  }

  return outputValue;
}
